import React, { useState, useCallback } from "react";
import { withTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import { useSMS } from "./hooks/useSMS";
import SMSSidebar from "./components/SMSSidebar";
import SMSChatWindow from "./components/SMSChatWindow";
import SMSModals from "./components/SMSModals";
import countriesData from "../../Config/countries.json";
import "./SMS.css";

const SMS = ({ t, match, history }) => {
    const {
        balance,
        loadingBalance,
        fetchBalance,
        fetchHistory,
        bills,
        loadingBills,
        fetchBills,
        activePhone,
        setActivePhone,
        sending,
        sendSMSMessage,
        userInfo,
        loadingUserInfo,
        fetchUserInfo,
        searchQuery,
        setSearchQuery,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        limit,
        setLimit,
        countryFilter,
        setCountryFilter,
        statusFilter,
        setStatusFilter,
        chatThreads,
        activeChatMessages,
        userCache
    } = useSMS(t);

    // Modal UI states
    const [showBillsModal, setShowBillsModal] = useState(false);
    const [showNewChatModal, setShowNewChatModal] = useState(false);
    const [showUserInfoModal, setShowUserInfoModal] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // Sync select phone parameter if passed in route (allowing deep-linking like WhatsApp)
    React.useEffect(() => {
        const phoneParam = match?.params?.phone;
        if (phoneParam) {
            const cleanPhone = phoneParam.replace(/\D/g, '');
            if (cleanPhone && cleanPhone !== activePhone) {
                setActivePhone(cleanPhone);
            }
        } else {
            if (activePhone) {
                setActivePhone(null);
            }
        }
    }, [match, activePhone, setActivePhone]);

    const handleSelectChat = useCallback((phone) => {
        if (history) {
            history.push(`/ControlPanel/SMS/${phone}`);
        } else {
            setActivePhone(phone);
        }
    }, [history, setActivePhone]);

    const handleShowUserInfo = useCallback((phone) => {
        setShowUserInfoModal(true);
        fetchUserInfo(phone);
    }, [fetchUserInfo]);

    const getCountryFlag = useCallback((countryCode) => {
        if (!countryCode) return "";
        const code = countryCode.toLowerCase();
        const found = countriesData.countries.find(c => c.value === code || c.alpha3?.toLowerCase() === code);
        return found ? found.icon : `/assets/flags/32x32/${code}.png`;
    }, []);

    return (
        <>
            <Helmet>
                <title>{t("Pages.SMS.title") || "رسائل SMS"} | {t("alarabeyya")}</title>
            </Helmet>

            <div className="sms-page-wrapper">
                {!sidebarOpen && (
                    <div
                        className="mobile-sidebar-toggle-btn d-md-none"
                        onClick={() => setSidebarOpen(true)}
                        title={t("Pages.Chatbot.OpenMenu") || "فتح القائمة"}
                    >
                        <i className="fal fa-bars"></i>
                    </div>
                )}
                <div className="sms-container">
                    <SMSSidebar
                        balance={balance}
                        loadingBalance={loadingBalance}
                        chatThreads={chatThreads}
                        activePhone={activePhone}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        dateFrom={dateFrom}
                        setDateFrom={setDateFrom}
                        dateTo={dateTo}
                        setDateTo={setDateTo}
                        limit={limit}
                        setLimit={setLimit}
                        countryFilter={countryFilter}
                        setCountryFilter={setCountryFilter}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        onSelectChat={handleSelectChat}
                        onBillsClick={() => setShowBillsModal(true)}
                        onNewChatClick={() => setShowNewChatModal(true)}
                        onSyncClick={() => {
                            fetchBalance();
                            fetchHistory(true);
                        }}
                        userCache={userCache}
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                        t={t}
                    />

                    {activePhone ? (
                        <SMSChatWindow
                            activePhone={activePhone}
                            messages={activeChatMessages}
                            sending={sending}
                            onSendMessage={(body) => sendSMSMessage(activePhone, body)}
                            onShowUserInfo={handleShowUserInfo}
                            onBackToList={() => {
                                if (history) {
                                    history.push('/ControlPanel/SMS');
                                } else {
                                    setActivePhone(null);
                                }
                            }}
                            userInfo={userInfo}
                            t={t}
                        />
                    ) : (
                        <div className="sms-welcome-screen">
                            <div className="sms-welcome-content">
                                <i className="fal fa-sms sms-welcome-icon"></i>
                                <h4 className="sms-welcome-title">{t("Pages.SMS.welcomeTitle") || "بوابة الرسائل النصية الإدارية"}</h4>
                                <p className="sms-welcome-text">
                                    {t("Pages.SMS.welcomeText") || "اختر رقم هاتف أو محادثة من القائمة الجانبية لعرض السجل ومتابعة المراسلات أو أرسل رسالة جديدة."}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <SMSModals
                showBillsModal={showBillsModal}
                setShowBillsModal={setShowBillsModal}
                showNewChatModal={showNewChatModal}
                setShowNewChatModal={setShowNewChatModal}
                showUserInfoModal={showUserInfoModal}
                setShowUserInfoModal={setShowUserInfoModal}
                userInfoData={userInfo}
                setUserInfoData={setActivePhone} // mock updates if needed
                loadingUserInfo={loadingUserInfo}
                bills={bills}
                loadingBills={loadingBills}
                fetchBills={fetchBills}
                sendSMSMessage={sendSMSMessage}
                getCountryFlag={getCountryFlag}
                t={t}
            />
        </>
    );
};

export default withTranslation('common')(SMS);
